<template>
  <section class="relative pt-24 pb-16 md:pt-28 md:pb-24 bg-[#1E2652]">
    <div class="container mx-auto px-4 md:px-6 max-w-6xl">
      <!-- Header -->
      <div class="mb-10 md:mb-14 text-center">
        <h1 class="text-3xl md:text-4xl font-bold text-white tracking-tight">
          Politique de Confidentialité
        </h1>
        <div class="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 text-gray-200 px-3 py-1 text-sm">
          <span class="h-2 w-2 rounded-full bg-[#B7E000]"></span>
          <span>Dernière mise à jour : 05 janvier 2026</span>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-12 gap-8">
        <!-- TOC -->
        <aside class="hidden md:block md:col-span-4 lg:col-span-3">
          <nav class="sticky top-24 bg-white/5 border border-white/10 rounded-xl p-4">
            <h2 class="text-sm font-semibold text-gray-200 mb-3">Sommaire</h2>
            <ul class="space-y-2">
              <li v-for="item in toc" :key="item.slug">
                <a :href="'#' + item.slug" class="toc-link block text-sm text-gray-300 hover:text-white">
                  {{ item.title }}
                </a>
              </li>
            </ul>
          </nav>
        </aside>

        <!-- Content -->
        <main class="md:col-span-8 lg:col-span-9">
          <div class="bg-white/[0.04] border border-white/10 rounded-2xl p-6 md:p-8">
            <section
              v-for="(section, index) in privacySections"
              :key="index"
              class="pt-4 first:pt-0 border-t border-white/10 first:border-t-0"
            >
              <h2
                class="text-xl md:text-2xl font-semibold text-[#B7E000] mb-3 scroll-mt-28"
                :id="toc[index].slug"
              >
                {{ section.title }}
              </h2>
              <div class="text-gray-200 leading-relaxed space-y-4" v-html="section.content"></div>
            </section>
          </div>

          <!-- Contact Section -->
          <div class="text-center mt-10">
            <h3 class="text-lg font-semibold text-white mb-3">Questions ?</h3>
            <p class="text-gray-300 mb-5">Pour toute question relative à notre politique de confidentialité, contactez-nous à :</p>
            <a
              href="mailto:support@padelgo.ma"
              class="inline-flex items-center px-6 py-3 rounded-full bg-[#B7E000] text-[#1E2652] font-medium hover:bg-[#B7E000]/90 transition-colors"
            >
              support@padelgo.ma
            </a>
          </div>
        </main>
      </div>
    </div>
  </section>
  </template>

<script setup lang="ts">
const privacySections = [
  {
    title: "1. Préambule",
    content: `
      <p>La présente Politique de Confidentialité explique comment « <strong>Rayan Marsile Corp, SARLAU immatriculée au registre de commerce de Rabat sous le N° RC 184873</strong> » (ci-après « PadelGo », « nous » ou « notre ») collecte, utilise, conserve et partage les données personnelles des utilisateurs de l'application mobile et web PadelGo (ci-après « l'Application »).</p>
      <p>Nous nous engageons à protéger la vie privée de nos utilisateurs conformément à la loi marocaine n°09-08 relative à la protection des données à caractère personnel et aux exigences de la Commission Nationale de contrôle de la protection des Données à caractère Personnel (CNDP).</p>
      <p>En utilisant l'Application, vous acceptez expressément les dispositions de la présente Politique.</p>
    `
  },
  {
    title: "2. Responsable du traitement et contact",
    content: `
      <p>Le responsable du traitement est <strong>Rayan Marsile Corp, SARLAU</strong>, dont le siège social est situé au <strong>45 avenue de France, Appt 8, Agdal, Rabat, Maroc</strong>, immatriculée au registre de commerce de Rabat sous le N° RC <strong>184873</strong>.</p>
      <p>Pour toute question relative à vos données ou pour exercer vos droits, vous pouvez nous contacter à l'adresse suivante : <strong><a class="underline text-[#B7E000]" href="mailto:support@padelgo.ma">support@padelgo.ma</a></strong>.</p>
    `
  },
  {
    title: "3. Données collectées",
    content: `
      <p>Lors de votre inscription et de l'utilisation de l'Application, nous collectons certaines données personnelles nécessaires à la fourniture de nos services.</p>
      <p>Les informations obligatoires comprennent : nom complet, adresse email, numéro de téléphone, genre/sexe, ville, statistiques de jeu, niveau (1 à 10) et préférences de jeu.</p>
      <p>Une photo de profil peut être ajoutée de manière facultative.</p>
      <p>Nous collectons également des données techniques, telles que votre adresse IP, le modèle de votre appareil et le système d'exploitation utilisé.</p>
      <p>Si vous l'autorisez, nous collectons votre position géographique à l'ouverture de l'Application afin de vous proposer les clubs les plus proches.</p>
      <p>Dans le cadre de la fonctionnalité de <strong>chat</strong>, nous collectons et stockons les messages que vous échangez avec d'autres Utilisateurs dans les chats liés aux matchs ou réservations. Ces messages peuvent être accessibles :</p>
      <ul class="list-disc list-inside space-y-2 mb-4">
        <li>Pour les matchs publics : à tous les Utilisateurs de l'Application</li>
        <li>Pour les matchs privés : uniquement aux Utilisateurs invités ou disposant du lien d'accès au match</li>
      </ul>
      <p>Votre historique de réservations, matchs et résultats est conservé pendant un an. Les messages de chat sont conservés à des fins de modération, de sécurité et de prévention des abus, conformément à la section 6 des Conditions Générales d'Utilisation. Les autres données de compte sont conservées tant que vous disposez d'un compte actif. En cas de demande de suppression, nous anonymisons vos données personnelles tout en conservant certaines informations à des fins statistiques.</p>
      <p>Dans le cadre de la fonctionnalité « J'ai déjà réservé un terrain », si vous créez un match public, votre numéro de téléphone sera affiché aux autres utilisateurs du match afin de faciliter l'organisation et la communication.</p>
    `
  },
  {
    title: "4. Finalités du traitement",
    content: `
      <p>Les données collectées sont utilisées pour :</p>
      <ul class="list-disc list-inside space-y-2">
        <li>Vous permettre d'accéder aux fonctionnalités de l'Application.</li>
        <li>Gérer vos réservations et matchs.</li>
        <li>Faciliter la communication entre Utilisateurs via la fonctionnalité de chat.</li>
        <li>Afficher les clubs à proximité de votre localisation.</li>
        <li>Améliorer nos services grâce à l'analyse des usages.</li>
        <li>Prévenir les fraudes ou abus, notamment via la modération des contenus de chat.</li>
        <li>Vous envoyer des <strong>communications commerciales</strong> de PadelGo et de ses partenaires (offres promotionnelles, actualités, événements, etc.) par email, SMS, notifications push ou tout autre moyen de communication électronique, conformément à votre consentement donné lors de l'acceptation des Conditions Générales d'Utilisation.</li>
      </ul>
      <p>Elles peuvent également être utilisées pour répondre à nos obligations légales.</p>
      <p>Vous pouvez retirer votre consentement aux communications commerciales à tout moment via les paramètres de l'Application, en cliquant sur le lien de désinscription dans nos emails, ou en nous contactant à <strong><a class="underline text-[#B7E000]" href="mailto:support@padelgo.ma">support@padelgo.ma</a></strong>.</p>
    `
  },
  {
    title: "5. Conservation des données",
    content: `
      <p>Vos données de compte sont conservées tant que vous utilisez nos services ou jusqu'à la demande de suppression. Les données relatives aux réservations, matchs et résultats sont conservées pendant un an à compter de leur enregistrement.</p>
      <p>Les données techniques ne sont pas stockées de manière permanente, leur consultation se faisant uniquement en temps réel pour le suivi technique.</p>
    `
  },
  {
    title: "6. Partage des données",
    content: `
      <p>Les données nécessaires à la réservation sont transmises aux clubs partenaires concernés, notamment votre nom, votre numéro de téléphone et les détails de la réservation.</p>
      <p>Les clubs partenaires ont également accès aux informations sur les joueurs ayant joué dans leur établissement, incluant : nom, adresse email, numéro de téléphone, ainsi que les statistiques de jeu réalisées au sein de leur club.</p>
      <p>Nous travaillons avec des prestataires techniques, notamment :</p>
      <ul class="list-disc list-inside space-y-2 mb-4">
        <li><strong>MongoDB Atlas</strong> (hébergé en Allemagne) pour la base de données.</li>
        <li><strong>Hetzner</strong> (hébergé en Allemagne) pour notre serveur.</li>
        <li><strong>Firebase</strong> (Google), utilisé pour l'envoi des notifications et le stockage des photos de profil, pouvant impliquer un traitement de données au sein de l'Union Européenne.</li>
      </ul>
      <p>Vos données peuvent être communiquées aux autorités compétentes si la loi nous y oblige ou sur demande judiciaire.</p>
    `
  },
  {
    title: "7. Transferts de données hors Maroc",
    content: `
      <p>Certains prestataires, notamment Firebase, peuvent traiter des données en dehors du Maroc et de l'Union Européenne. Dans ce cas, nous veillons à ce que des garanties appropriées soient mises en place pour assurer la protection de vos informations.</p>
    `
  },
  {
    title: "8. Sécurité",
    content: `
      <p>Nous mettons en œuvre des mesures techniques et organisationnelles destinées à protéger vos données contre toute perte, destruction, altération ou accès non autorisé. Cela inclut le chiffrement des communications via TLS, la restriction d'accès interne aux seules personnes autorisées et la mise en place de sauvegardes régulières.</p>
    `
  },
  {
    title: "9. Vos droits",
    content: `
      <p>Conformément à la loi n°09-08, vous disposez d'un droit d'accès, de rectification, d'opposition, de suppression ainsi que du droit de retirer votre consentement à tout moment.</p>
      <p>Pour exercer ces droits, il vous suffit de nous contacter à l'adresse suivante : <strong><a class="underline text-[#B7E000]" href="mailto:support@padelgo.ma">support@padelgo.ma</a></strong>.</p>
    `
  },
  {
    title: "10. Modifications de la Politique",
    content: `
      <p>PadelGo se réserve le droit de modifier la présente Politique de Confidentialité à tout moment. Toute modification sera notifiée aux utilisateurs par email avant son entrée en vigueur.</p>
      <p>L'utilisation continue de l'Application après modification vaut acceptation de la nouvelle version.</p>
    `
  }
]

function slugifyTitle(title: string): string {
  return title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

const toc = privacySections.map((s) => ({ title: s.title, slug: slugifyTitle(s.title) }))
</script>

<style scoped>
.toc-link {
  transition: color 0.15s ease;
}
</style>